<template>
  <AppLayout>
    <Head :title="`Archiwum — ${board.name}`" />

    <div class="col-12 layout-spacing">
      <div class="widget widget-six">
        <!-- Nagłówek -->
        <div class="widget-heading">
          <div class="row align-items-center">
            <div class="col-12 col-lg-7">
              <h4 class="mb-2">
                <i class="bi bi-archive me-2"></i>
                Archiwum kart
              </h4>

              <p class="text-muted mb-0">
                Tablica:
                <span class="fw-semibold">{{ board.name }}</span>
              </p>
            </div>

            <div class="col-12 col-lg-5 mt-3 mt-lg-0 text-lg-end">
              <Link
                :href="`/kanban/boards-all/${board.id}`"
                class="btn btn-outline-secondary btn-sm"
              >
                <i class="bi bi-arrow-left me-1"></i>
                Wróć do tablicy
              </Link>
            </div>
          </div>
        </div>

        <!-- Zawartość -->
        <div class="widget-content">
          <div class="custom-table">
            <div
              class="d-flex align-items-center justify-content-between mb-3"
            >
              <h5 class="mb-0">Zarchiwizowane karty</h5>

              <span class="badge bg-secondary">
                {{ cards.total ?? cards.data.length }}
              </span>
            </div>

            <!-- Puste archiwum -->
            <div
              v-if="cards.data.length === 0"
              class="text-center py-5"
            >
              <i class="bi bi-inbox archived-empty-icon"></i>

              <h5 class="mt-3">
                Brak zarchiwizowanych kart
              </h5>

              <p class="text-muted mb-3">
                Karty zarchiwizowane na tej tablicy pojawią się tutaj.
              </p>

              <Link
                :href="`/kanban/boards-all/${board.id}`"
                class="btn btn-primary btn-sm"
              >
                <i class="bi bi-kanban me-1"></i>
                Wróć do tablicy
              </Link>
            </div>

            <!-- Lista kart -->
            <div v-else class="table-responsive">
              <table class="table table-hover align-middle">
                <thead>
                  <tr>
                    <th style="width: 60px">#</th>
                    <th>Tytuł karty</th>
                    <th>Kolumna</th>
                    <th>Utworzył</th>
                    <th>Data archiwizacji</th>
                    <th class="text-end">Akcje</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="(card, index) in cards.data"
                    :key="card.id"
                  >
                    <td>
                      {{ (cards.from ?? 1) + index }}
                    </td>

                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <span class="archived-card-icon">
                          <i class="bi bi-card-text"></i>
                        </span>

                        <div class="min-w-0">
                          <div class="fw-semibold archived-card-title">
                            {{ card.title }}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span
                        v-if="card.column?.name"
                        class="badge bg-light text-dark border"
                      >
                        {{ card.column.name }}
                      </span>

                      <span v-else class="text-muted small">
                        Nieznana
                      </span>
                    </td>

                    <td>
                      <span class="text-muted">
                        {{ card.creator?.name ?? 'Nieznany użytkownik' }}
                      </span>
                    </td>

                    <td>
                      <span class="text-muted small">
                        <i class="bi bi-calendar3 me-1"></i>
                        {{ formatDate(card.archived_at) }}
                      </span>
                    </td>

                    <td class="text-end">
                      <button
                        type="button"
                        class="btn btn-success btn-sm"
                        :disabled="restoringId === card.id"
                        @click="restoreCard(card)"
                      >
                        <i
                          :class="restoringId === card.id
                            ? 'bi bi-arrow-repeat me-1'
                            : 'bi bi-arrow-counterclockwise me-1'"
                        ></i>

                        {{
                          restoringId === card.id
                            ? 'Przywracanie...'
                            : 'Przywróć'
                        }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Paginacja -->
          <nav
            v-if="cards.links && cards.links.length > 3"
            class="mt-4"
            aria-label="Stronicowanie zarchiwizowanych kart"
          >
            <div
              class="d-flex flex-wrap align-items-center justify-content-between gap-3"
            >
              <p class="text-muted small mb-0">
                <template v-if="cards.total">
                  Wyświetlono {{ cards.from ?? 0 }}–{{ cards.to ?? 0 }}
                  z {{ cards.total }} kart
                </template>

                <template v-else>
                  Lista zarchiwizowanych kart
                </template>
              </p>

              <div class="d-flex flex-wrap gap-1">
                <template
                  v-for="(link, index) in cards.links"
                  :key="index"
                >
                  <Link
                    v-if="link.url"
                    :href="link.url"
                    preserve-scroll
                    class="btn btn-sm"
                    :class="link.active
                      ? 'btn-primary'
                      : 'btn-outline-secondary'"
                    v-html="link.label"
                  />

                  <span
                    v-else
                    class="btn btn-sm btn-outline-secondary disabled"
                    v-html="link.label"
                  ></span>
                </template>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/Layouts/App.vue'
import { Head, Link, router } from '@inertiajs/vue3'

export default {
  components: {
    AppLayout,
    Head,
    Link,
  },

  props: {
    board: {
      type: Object,
      required: true,
    },

    cards: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      restoringId: null,
    }
  },

  methods: {
    restoreCard(card) {
      if (this.restoringId !== null) return

      if (!confirm(`Przywrócić kartę „${card.title}”?`)) {
        return
      }

      this.restoringId = card.id

      router.patch(
        `/kanban/boards/${this.board.id}/cards/${card.id}/restore`,
        {},
        {
          preserveScroll: true,
          onFinish: () => {
            this.restoringId = null
          },
        }
      )
    },

    formatDate(value) {
      if (!value) return '—'

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return '—'
      }

      return new Intl.DateTimeFormat('pl-PL', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date)
    },
  },
}
</script>

<style scoped>
.archived-card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 1.1rem;
}

.archived-card-title {
  overflow-wrap: anywhere;
}

.archived-empty-icon {
  font-size: 2.5rem;
  color: #adb5bd;
}

.min-w-0 {
  min-width: 0;
}

@media (max-width: 767.98px) {
  .table-responsive {
    font-size: 0.875rem;
  }
}
</style>